import { Fragment, useEffect, useState } from "react";
import "./App.css";
import {
  BrowserRouter,
  Switch,
  useParams,
  Route,
  Link,
} from "react-router-dom";

import recipeData from "./data.json";

import { sortVolumes } from "./Util";

function transformImageUrl(url) {
  return url.replace("https://nutrition-assets.freeletics.com/recipes/", "/images/");
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Switch>
          <Route exact path="/">
            <RecipeList />
          </Route>
          <Route path="/recipe/:id">
            <Recipe />
          </Route>
        </Switch>
      </BrowserRouter>
    </div>
  );
}

function RecipeList() {
  const [recipes, setRecipes] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const savedScrollY = sessionStorage.getItem("recipeListScrollY");
    if (savedScrollY !== null) {
      window.scrollTo(0, Number(savedScrollY));
    }
  }, []);

  const handleRecipeOpen = () => {
    sessionStorage.setItem("recipeListScrollY", String(window.scrollY));
  };

  useEffect(() => {
    const filteredRecipes = recipeData.filter(
      (item) =>
        item.type === "default" &&
        item.name.toLowerCase().includes(filter.toLowerCase())
    );
    setRecipes(filteredRecipes);
  }, [filter]);

  return (
    <div className="recipeList">
      <div className="filterArea">
        <input
          id="nameFilter"
          placeholder="Suche..."
          onChange={(e) => setFilter(e.target.value)}
        ></input>
      </div>
      {recipes.map((recipe) => {
        return (
          <Link key={recipe._id} to={`/recipe/${recipe._id}`} onClick={handleRecipeOpen}>
            <div className="recipeTileWrapper">
              <div
                className="recipeTile"
                key={recipe._id}
                style={{
                  backgroundImage:
                    "linear-gradient(to bottom, rgba(255,255,255,0), rgba(0,0,0,0.8)), url(" +
                    `${transformImageUrl(recipe.image_urls.low)}` +
                    ")",
                }}
              >
                <span className="name">{recipe.name}</span>
                <br />
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function Recipe() {
  let { id } = useParams();

  const [recipe, setRecipe] = useState({});

  const fetchRecipe = (fetchId) => {
    const data = recipeData.find((item) => item._id === fetchId);
    if (!data) {
      setRecipe({});
      return;
    }
    data.volumes = sortVolumes(data.volumes);
    setRecipe(data);
  };

  useEffect(() => {
    fetchRecipe(id);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [id]);

  const renderNumberColumn = (number, unit, decimals = undefined, key = undefined) => {
    if (typeof number === "number" && decimals !== undefined) {
      number = number.toFixed(decimals);
    }
    let firstPart = Math.trunc(number);
    let secondPart = number - Math.trunc(number);
    if (secondPart === 0.5) {
      if (firstPart === 0) {
        firstPart = "";
      }
      secondPart = "½";
    } else if (secondPart === 0.25) {
      if (firstPart === 0) {
        firstPart = "";
      }
      secondPart = "¼";
    } else if (secondPart === 0) {
      secondPart = "";
    } else {
      secondPart = (secondPart + "").substring(1);
    }

    let thirdPart = unit;

    if (Number.isNaN(firstPart)) {
      firstPart = "";
    }

    return (
      <Fragment key={key}>
        <td className="wholeNumber">{firstPart}</td>
        <td className="fractionNumber">{secondPart}</td>
        <td className="unit">{thirdPart}</td>
      </Fragment>
    );
  };

  return (
    <div className="recipe">
      <Link to="/" className="backNavigation">
        &larr; Zurück
      </Link>
      {recipe && Object.keys(recipe).length !== 0 && (
        <>
          <img className="recipeImage" src={transformImageUrl(recipe.image_urls.medium)} alt={recipe.name} />

          <div className="recipeName">{recipe.name}</div>
          <div className="shortDescription">{recipe.short_description}</div>
          <div className="nutVal">
            <h2>Nährstoffe</h2>
            <div className="tableSwipeHint">↔ für weitere Spalten wischen</div>
            <div className="detailTableScroller">
              <table>
                <colgroup>
                  <col className="nutritionName"></col>
                  {sortVolumes(recipe.volumes).map((volume) => {
                    return <col key={volume} className="amount"></col>;
                  })}
                </colgroup>
                <thead>
                  <tr>
                    <th></th>
                    {sortVolumes(recipe.volumes).map((volume) => {
                      return (
                        <th key={volume} className="amount" colSpan="3">
                          {volume}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Kohlenhydrate</th>
                    {sortVolumes(recipe.volumes).map((volume) => {
                      return renderNumberColumn(
                        recipe.carbohydrate_g[volume],
                        "",
                        0,
                        `carbohydrate-${volume}`
                      );
                    })}
                  </tr>
                  <tr>
                    <th>Fett</th>
                    {recipe.volumes.map((volume) => {
                      return renderNumberColumn(recipe.fat_g[volume], "", 0, `fat-${volume}`);
                    })}
                  </tr>
                  <tr>
                    <th>Eiweiß</th>
                    {recipe.volumes.map((volume) => {
                      return renderNumberColumn(
                        recipe.protein_g[volume],
                        "",
                        0,
                        `protein-${volume}`
                      );
                    })}
                  </tr>
                  <tr>
                    <th>kcal</th>
                    {recipe.volumes.map((volume) => {
                      return renderNumberColumn(recipe.kcal[volume], "", 0, `kcal-${volume}`);
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="ingredients">
            <h2>Zutaten</h2>
            <div className="tableSwipeHint">↔ für weitere Spalten wischen</div>
            <div className="detailTableScroller">
              <table>
                <colgroup>
                  <col className="nutritionName"></col>
                  {sortVolumes(recipe.volumes).map((volume) => {
                    return <col key={volume} className="amount"></col>;
                  })}
                </colgroup>
                <thead>
                  <tr>
                    <th></th>
                    {recipe.volumes.map((volume) => {
                      return <th key={volume} colSpan="3">{volume}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {recipe.ingredients.map((ingredient) => {
                    return (
                      <tr key={ingredient.id}>
                        <th className="ingredientName">{ingredient.name}</th>
                        {recipe.volumes.map((volume) => {
                          return renderNumberColumn(
                            ingredient.amounts[volume],
                            ingredient.unit,
                            undefined,
                            `${ingredient.id}-${volume}`
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="steps">
            <h2>Zubereitung</h2>
            <ul>
              {recipe.steps.map((step) => {
                return <li key={step}>{step}</li>;
              })}
            </ul>
          </div>
          {recipe.hasOwnProperty("seasonings") && recipe.seasonings.length > 0 && (
            <div className="seasonings">
              <h2>Gewürze</h2>
              <ul>
                {recipe.seasonings.map((seasoning) => {
                  return <li key={seasoning}>{seasoning}</li>;
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default App;
export { RecipeList, Recipe };
